import { Loader2, Search } from "lucide-react";
import { useState } from "react";

import { AdminNavLink } from "@/components/admin-nav-link";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  type AssistantTurn,
  type EscalationOutcome,
  type GenerationResult,
  type SocialAdSet,
  type SpecCorrection,
  useComposerAsk,
  useComposerConfirmDeck,
  useComposerConfirmEscalation,
  useComposerConfirmFormats,
  useComposerConfirmPages,
  useComposerConfirmRevision,
  useComposerEscalations,
  useComposerRemoveTurn,
  useComposerRenameTurn,
  useComposerRules,
  useComposerTranslationStatus,
  useComposerTurns,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { AskBar } from "./ask-bar";
import { ConversationElements } from "./conversation-elements";
import { DraftView } from "./draft-view";
import { ElementFrame } from "./element-view";
import { ErrorAlert } from "./error-alert";
import { GuardrailReport } from "./guardrail-report";
import { CollapsedRail, Rail } from "./rail";
import { RemoveRequest } from "./remove-request";
import { RulesView } from "./rules-view";
import { SlidePreviewPane } from "./slide-preview";
import type { FormatConfirmation } from "./social-ad-plan";
import { TurnCard } from "./turn-card";
import { LANGUAGE_NAMES, socialAdsOf } from "./utils";
import type { RailView } from "./views";

/**
 * Surface FastAPI's `detail` when there is one.
 *
 * The client's error interceptor wraps every non-OK response as
 * `{ status, body }`, so the useful sentence — "no materials to work from", say
 * — lives in `body.detail` rather than on an `Error`.
 */
function errorMessage(error: unknown): string {
  const body = (error as { body?: { detail?: unknown } } | null)?.body;
  if (typeof body?.detail === "string") {
    return body.detail;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Something went wrong. Try asking again.";
}

// Openings, not requests. The old set named the two PDFs the library happened
// to hold at the time — clicking one asked about material a market may not
// have, and it read as though the tool needed a precisely-worded sentence
// rather than a plain description. These are the first half of a request, left
// for the requestor to finish, and between them they reach each outcome the
// composer can decide: hand over an approved asset, build a deck, keep and
// translate the pages of a document, or lay out a social ad. The second deck
// opening gave way to the ad one — a route nobody will guess exists is worth
// more than a second example of a route already shown.
const EXAMPLES = [
  "Do you have an image of ",
  "A 5-slide deck for architects on ",
  "Translate into German the pages about ",
  "A social ad for Instagram about ",
];

/**
 * The composer — one conversation, four possible outcomes.
 *
 * The requestor asks what asset they are looking for. The composer searches the
 * asset database (the inbox folder today, a real DAM later), decides what it can
 * honestly do, and says so: hand over an approved asset, build the deck, propose
 * pages to keep and translate them, or draft a brief for Strategic Comms.
 *
 * Deliberately absent: any way to browse assets, choose a mode, or pick an edit
 * type. Those are the composer's job to work out, not the requestor's.
 */
export function ComposerPage() {
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [pageResults, setPageResults] = useState<Record<string, GenerationResult>>({});
  // Ads rendered in this session, keyed by the turn that asked for them. The
  // turn itself carries them once the history reloads; this is what makes them
  // appear the moment the render finishes.
  const [socialAdSets, setSocialAdSets] = useState<Record<string, SocialAdSet>>({});
  const [escalationOutcomes, setEscalationOutcomes] = useState<Record<string, EscalationOutcome>>(
    {},
  );
  // The rail holds reference material — what the run did, and what has been
  // drafted for Strategic Comms. Neither is needed to ask a question, so it
  // collapses out of the way and the conversation keeps the centre.
  const [railOpen, setRailOpen] = useState(true);
  const [view, setView] = useState<RailView>({ kind: "chat" });

  const ask = useComposerAsk();
  const confirmPages = useComposerConfirmPages();
  const confirmDeck = useComposerConfirmDeck();
  const confirmFormats = useComposerConfirmFormats();
  const confirmRevision = useComposerConfirmRevision();
  const confirmEscalation = useComposerConfirmEscalation();
  const removeTurn = useComposerRemoveTurn();
  const renameTurn = useComposerRenameTurn();
  const { data: translationStatus } = useComposerTranslationStatus();
  const { data: escalations } = useComposerEscalations();
  const { data: rules } = useComposerRules();
  // Wider than the rail has rows for, on purpose. The rail groups turns into
  // conversations, so more turns no longer means more rows — and a follow-up
  // whose opening request fell outside the window has nothing to group with,
  // which puts it back in the list as a request of its own.
  const { data: recentTurns } = useComposerTurns(30);

  const activeDraft =
    view.kind === "draft" ? escalations?.find((item) => item.id === view.id) : undefined;

  // Elements are per turn, so a report or file view names the turn it came
  // from rather than always showing the newest artifact.
  const viewedTurnId = view.kind === "run-report" || view.kind === "file" ? view.turnId : undefined;
  // The rail lists a conversation's files as versions, which reaches rounds
  // that are not on screen — opening the app shows only the latest round. So
  // the remembered history is the fallback, or picking v1 of a conversation
  // would find nothing and quietly show the panel's empty state instead.
  const viewedTurn = viewedTurnId
    ? (turns.find((t) => t.turn_id === viewedTurnId) ??
      recentTurns?.find((t) => t.turn_id === viewedTurnId))
    : undefined;
  const viewedResult = viewedTurn
    ? (pageResults[viewedTurn.turn_id] ?? viewedTurn.result ?? undefined)
    : undefined;

  // Opening a past request replaces what the main column shows. Nothing is
  // lost: every turn is on the server, and the rail keeps a row for the
  // conversation it belongs to, so this is navigation rather than a
  // destructive switch.
  const handleOpenRequest = (turn: AssistantTurn) => {
    setTurns([turn]);
    setView({ kind: "chat" });
  };

  // Removing a request is confirmed against what it actually holds, so the
  // turn waits here until the dialog comes back with an answer.
  const [removing, setRemoving] = useState<AssistantTurn | undefined>(undefined);

  const handleRemoveRequest = () => {
    if (!removing) {
      return;
    }
    const turnId = removing.turn_id;
    removeTurn.mutate(
      { turnId },
      {
        onSuccess: () => {
          setRemoving(undefined);
          // The open conversation may be the one just removed.
          setTurns((current) => current.filter((item) => item.turn_id !== turnId));
        },
      },
    );
  };

  // Renaming is not confirmed: an empty name puts the derived title back, so
  // the action undoes itself and there is nothing to warn about.
  const handleRenameRequest = (turn: AssistantTurn, name: string) => {
    renameTurn.mutate({ turnId: turn.turn_id, name });
  };

  const handleNewRequest = () => {
    setTurns([]);
    setView({ kind: "chat" });
  };

  // Asking inside an open conversation continues it: the composer is told
  // which request this one follows, so "now the same in French" knows what
  // "the same" refers to instead of searching the library for the word "this".
  const handleAsk = (requestText: string) => {
    ask.mutate(
      { requestText, previousTurnId: turns.at(-1)?.turn_id },
      { onSuccess: (turn) => turn && setTurns((current) => [...current, turn]) },
    );
  };

  // A correction is the same question asked again with a control pinned, so it
  // replaces the turn it came from rather than stacking a near-identical one
  // underneath it. The thread stays a conversation, not a list of retries.
  const handleCorrect = (turn: AssistantTurn, corrections: SpecCorrection) => {
    ask.mutate(
      { requestText: turn.request_text, corrections },
      {
        onSuccess: (corrected) =>
          corrected &&
          setTurns((current) =>
            current.map((item) => (item.turn_id === turn.turn_id ? corrected : item)),
          ),
      },
    );
  };

  const handleConfirmDeck = (turn: AssistantTurn) => {
    confirmDeck.mutate(
      { turnId: turn.turn_id },
      {
        onSuccess: (result) =>
          result &&
          setTurns((current) =>
            current.map((item) =>
              item.turn_id === turn.turn_id ? { ...item, result, awaiting: "nothing" } : item,
            ),
          ),
      },
    );
  };

  // Rebuilding with only the named edits applied. The result replaces the deck
  // on the turn exactly as a first build does, so the rail lists it as the next
  // version of the conversation rather than as an unrelated file.
  const handleConfirmRevision = (turn: AssistantTurn) => {
    confirmRevision.mutate(
      { turnId: turn.turn_id },
      {
        onSuccess: (result) =>
          result &&
          setTurns((current) =>
            current.map((item) =>
              item.turn_id === turn.turn_id ? { ...item, result, awaiting: "nothing" } : item,
            ),
          ),
      },
    );
  };

  // The same content, asked for as a deck. Phrased as a fresh request rather
  // than a special mode: it routes through the normal decision path, so the
  // rebuild is subject to exactly the same guardrails as any other ask.
  const handleRebuildAsDeck = (turn: AssistantTurn) => {
    const subject = turn.document?.filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
    const language = LANGUAGE_NAMES[turn.spec?.target_language ?? "DE"] ?? "German";
    handleAsk(`A deck on ${subject} in ${language}, ${turn.spec?.audience ?? "prospect"} audience`);
  };

  const handleConfirmPages = (turn: AssistantTurn, pages: number[]) => {
    confirmPages.mutate(
      { turnId: turn.turn_id, pages },
      {
        onSuccess: (result) =>
          result && setPageResults((current) => ({ ...current, [turn.turn_id]: result })),
      },
    );
  };

  // The ads are a version of the conversation like any other file, so the
  // result goes onto the turn exactly as a built deck does — that is what puts
  // it in the rail's version list and its guardrail report beside the thread.
  const handleConfirmFormats = (turn: AssistantTurn, confirmation: FormatConfirmation) => {
    confirmFormats.mutate(
      { turnId: turn.turn_id, ...confirmation },
      {
        onSuccess: (result) => {
          if (!result) {
            return;
          }
          setTurns((current) =>
            current.map((item) =>
              item.turn_id === turn.turn_id ? { ...item, result, awaiting: "nothing" } : item,
            ),
          );
          const ads = socialAdsOf(result);
          if (ads) {
            setSocialAdSets((current) => ({ ...current, [turn.turn_id]: ads }));
          }
        },
      },
    );
  };

  // A clarification option is a whole request, written to be sent as it stands,
  // so it goes down the ordinary ask path — same endpoint, same guardrails,
  // same echo in the thread. Nothing marks it as having come from a button,
  // because nothing about it is different from typing the sentence out.
  const handleChooseClarification = (option: string) => {
    logger.debug("[composer] clarification option chosen", option);
    handleAsk(option);
  };

  const handleConfirmEscalation = (turn: AssistantTurn, confirmed: boolean, note: string) => {
    confirmEscalation.mutate(
      { turnId: turn.turn_id, confirmed, note },
      {
        onSuccess: (outcome) =>
          outcome && setEscalationOutcomes((current) => ({ ...current, [turn.turn_id]: outcome })),
      },
    );
  };

  const error =
    ask.error ??
    confirmDeck.error ??
    confirmRevision.error ??
    confirmPages.error ??
    confirmFormats.error ??
    confirmEscalation.error;
  const isEmpty = turns.length === 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="bg-background flex h-screen flex-col">
        <header className="border-border bg-card z-30 flex shrink-0 items-center gap-3 border-b px-4 py-2.5">
          <img
            src="/brand/velux-logo.svg"
            alt="VELUX"
            className="h-7 w-auto shrink-0"
            width={127}
            height={42}
          />

          <div className="min-w-0 flex-1 text-right">
            <h1 className="font-heading text-foreground truncate text-base font-bold">
              Asset Composer
            </h1>
          </div>

          {/* Admins only — the link is absent for everyone else. */}
          <AdminNavLink className="-mr-1" />
        </header>

        <div className="flex min-h-0 flex-1">
          {/*
           * Closed, the rail is a dark icon edge rather than nothing: the
           * sections stay reachable in one click instead of needing the header
           * toggle first.
           */}
          <aside className="bg-rail shrink-0">
            {railOpen ? (
              <div className="h-full w-[22rem]">
                <Rail
                  view={view}
                  onSelect={setView}
                  rules={rules ?? []}
                  recentTurns={recentTurns ?? []}
                  // The newest round on screen, however many are open. The
                  // rail lists conversations rather than turns now, so a
                  // continued thread still has exactly one row to highlight —
                  // it used to give up as soon as a second round arrived.
                  activeTurnId={turns.at(-1)?.turn_id}
                  onOpenRequest={handleOpenRequest}
                  onRemoveRequest={setRemoving}
                  onRenameRequest={handleRenameRequest}
                  onNewRequest={handleNewRequest}
                  translationStatus={translationStatus}
                  onCollapse={() => setRailOpen(false)}
                />
              </div>
            ) : (
              <CollapsedRail view={view} onSelect={setView} onOpen={() => setRailOpen(true)} />
            )}
          </aside>

          <main className="min-w-0 flex-1 overflow-y-auto">
            <div className="mx-auto max-w-3xl min-w-0 space-y-6 px-6 py-8">
              {view.kind === "rules" ? (
                <RulesView category={view.category} />
              ) : view.kind === "run-report" ? (
                <ElementFrame
                  eyebrow="Guardrail report"
                  title={viewedResult?.filename ?? "This run"}
                  onBack={() => setView({ kind: "chat" })}
                >
                  <p className="text-muted -mt-2 max-w-2xl text-sm leading-6">
                    What the composer did to produce this file — every rule it applied, everything
                    it blocked, and everything it handed to Strategic Comms.
                  </p>
                  <GuardrailReport
                    generation={viewedResult}
                    isLoading={false}
                    onOpenRules={() => setView({ kind: "rules", category: "brand" })}
                  />
                </ElementFrame>
              ) : view.kind === "draft" ? (
                activeDraft ? (
                  <DraftView escalation={activeDraft} onBack={() => setView({ kind: "chat" })} />
                ) : (
                  <p className="text-muted py-8 text-sm">That draft is no longer available.</p>
                )
              ) : isEmpty ? (
                <section className="py-10 text-center sm:py-16">
                  <span className="bg-velux-tint text-velux mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full">
                    <Search className="h-5 w-5" />
                  </span>
                  <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                    What asset are you looking for?
                  </h2>
                  <p className="text-muted mx-auto mt-3 max-w-xl text-sm leading-6">
                    Describe it in plain language. I&apos;ll search the approved asset library and
                    either hand you what exists, build what you need on the VELUX template, or tell
                    you straight when it needs the design team.
                  </p>
                  <div className="mx-auto mt-8 max-w-2xl text-left">
                    <AskBar
                      onAsk={handleAsk}
                      isBusy={ask.isPending}
                      examples={EXAMPLES}
                      autoFocus
                      placeholder="Describe what you need — a deck, an image, a document in another language"
                    />
                  </div>
                </section>
              ) : (
                <>
                  <div className="space-y-8">
                    {turns.map((turn) => (
                      <TurnCard
                        key={turn.turn_id}
                        turn={turn}
                        pageResult={pageResults[turn.turn_id]}
                        socialAds={socialAdSets[turn.turn_id]}
                        escalationOutcome={escalationOutcomes[turn.turn_id]}
                        onConfirmDeck={() => handleConfirmDeck(turn)}
                        onConfirmRevision={() => handleConfirmRevision(turn)}
                        onCorrect={(corrections) => handleCorrect(turn, corrections)}
                        onConfirmPages={(pages) => handleConfirmPages(turn, pages)}
                        onConfirmFormats={(confirmation) =>
                          handleConfirmFormats(turn, confirmation)
                        }
                        onRebuildAsDeck={() => handleRebuildAsDeck(turn)}
                        onChooseClarification={handleChooseClarification}
                        onConfirmEscalation={(confirmed, note) =>
                          handleConfirmEscalation(turn, confirmed, note)
                        }
                        isConfirmingDeck={confirmDeck.isPending}
                        isConfirmingRevision={confirmRevision.isPending}
                        isAsking={ask.isPending}
                        isConfirmingPages={confirmPages.isPending}
                        isConfirmingFormats={confirmFormats.isPending}
                        isConfirmingEscalation={confirmEscalation.isPending}
                      />
                    ))}
                  </div>

                  {ask.isPending && (
                    <p className="text-muted flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Searching the asset library…
                    </p>
                  )}

                  {/* No rule above the bar: it is sticky, so the line sat on
                      top of whatever was scrolling under it. The gradient
                      already separates the two, and it does it by fading
                      rather than by cutting across the message. */}
                  <div className="sticky bottom-0 -mx-1 bg-gradient-to-t from-[var(--color-background)] from-60% via-[var(--color-background)] to-transparent px-1 pt-6 pb-2">
                    <AskBar
                      onAsk={handleAsk}
                      isBusy={ask.isPending}
                      placeholder="Ask for something else…"
                    />
                  </div>
                </>
              )}

              {error && (
                <ErrorAlert
                  message={errorMessage(error)}
                  onDismiss={() => {
                    ask.reset();
                    confirmDeck.reset();
                    confirmRevision.reset();
                    confirmPages.reset();
                    confirmFormats.reset();
                    confirmEscalation.reset();
                  }}
                />
              )}
            </div>
          </main>

          {/*
           * Only once the conversation has produced something. An empty panel
           * beside an empty conversation is furniture pretending to be content.
           */}
          {!isEmpty && (
            <aside
              className={`border-border bg-card hidden shrink-0 border-l lg:block ${
                view.kind === "file" ? "w-[26rem]" : "w-72"
              }`}
            >
              {view.kind === "file" && viewedResult ? (
                <SlidePreviewPane result={viewedResult} onBack={() => setView({ kind: "chat" })} />
              ) : (
                <ConversationElements
                  turns={turns}
                  pageResults={pageResults}
                  escalations={escalations ?? []}
                  view={view}
                  onSelect={setView}
                />
              )}
            </aside>
          )}
        </div>
        <RemoveRequest
          turn={removing}
          onCancel={() => setRemoving(undefined)}
          onConfirm={handleRemoveRequest}
          isBusy={removeTurn.isPending}
        />
      </div>
    </TooltipProvider>
  );
}
